import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart, BarChart } from 'echarts/charts';
import {
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
  ToolboxComponent,
  TitleComponent,
  GraphicComponent
} from 'echarts/components';
import { chartTheme, THEME_LIGHT } from '@rdrive/theme';

echarts.use([
  CanvasRenderer,
  LineChart,
  BarChart,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
  ToolboxComponent,
  TitleComponent,
  // an option drawn as `graphic` elements (StatusBreakdownCard's card replica)
  GraphicComponent
]);

// ECharts' own grid defaults (GridModel.defaultOption) — what an unset grid edge means
const GRID_TOP_DEFAULT = 60;
const GRID_BOTTOM_DEFAULT = 70;
// The title band above the plot: one line, or a title plus a subtitle line
const TITLE_BAND = 38;
const TITLE_BAND_WITH_SUBTITLE = 58;

const asArray = (value) => (value == null ? [] : Array.isArray(value) ? value : [value]);

function truncateLegendText(text, maxWidth) {
  /**
   * Truncates legend text to fit within max width
   * @param {string} text - Legend text to truncate
   * @param {number} maxWidth - Maximum width in pixels (approx)
   * @returns {string} Truncated text with ellipsis if needed
   */
  // Assume 8px per character average
  const maxChars = Math.floor(maxWidth / 8);
  if (text.length > maxChars) {
    return text.substring(0, Math.max(1, maxChars - 3)) + '...';
  }
  return text;
}

/**
 * A grid/box edge in px against the live chart's `total` height: a number as-is, `'12px'` or
 * `'12'` → 12, `'12%'` → total × 12 / 100, anything unset or unreadable → `fallback`.
 */
function toPx(value, total, fallback) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const text = value.trim();
    const number = parseFloat(text);
    if (Number.isFinite(number)) {
      if (text.endsWith('%')) return (total * number) / 100;
      if (/^-?[\d.]+(px)?$/.test(text)) return number;
    }
  }
  return fallback;
}

// A series' own colour, when it is a plain string (a colour FUNCTION or a gradient object — e.g.
// BaseBarChart's `colorBy: 'bar'` — has no single legend colour)
function seriesColour(series) {
  const colour = series.itemStyle?.color || series.lineStyle?.color;
  return typeof colour === 'string' ? colour : null;
}

function groupLegendByColor(series, fallbackColor) {
  /**
   * Groups series by colour and creates one combined legend entry per colour with the
   * de-duplicated series names. Keeps every series; the FIRST series of each colour group is
   * renamed to the group's legend name (so the legend entry matches it), the others lose their
   * legend hover link.
   * @param {Array} series - Array of series from chart option
   * @param {string} fallbackColor - the colour of a series without a plain-string colour
   * @returns {Object} Object with groupedLegend data and updated series array
   */
  const colorGroups = new Map();

  // Group series by their colour (case-insensitively), maintaining order
  series.forEach((ser, index) => {
    const color = seriesColour(ser) || fallbackColor;
    const key = color.toLowerCase();
    if (!colorGroups.has(key)) colorGroups.set(key, { color, items: [] });
    colorGroups.get(key).items.push({ name: ser.name, index });
  });

  const groupedLegend = [];
  const updatedSeries = series.map((ser) => ({ ...ser }));

  colorGroups.forEach(({ color, items }) => {
    // Unique series names for this colour, without colour codes standing in for a name
    const uniqueNames = [...new Set(items.map((item) => item.name))]
      .filter((name) => typeof name === 'string' && name !== '' && !name.startsWith('#'));

    // Use the names if there are any, otherwise the colour
    const combinedName = uniqueNames.length > 0 ? uniqueNames.join(', ') : color;

    groupedLegend.push({
      name: combinedName,
      icon: 'circle',
      itemStyle: { color }
    });

    // The legend entry is matched to its group's first series by name — mapped by the group,
    // never by position, so two series sharing a colour cannot shift the names
    updatedSeries[items[0].index].name = combinedName;
    items.slice(1).forEach((item) => {
      updatedSeries[item.index].legendHoverLink = false;
    });
  });

  return { groupedLegend, updatedSeries };
}

function calculateLegendHeight(legendData, chartWidth) {
  /**
   * Estimates the height needed for the legend based on content and chart width
   * @param {Array} legendData - Array of legend items (`name` = the text the legend shows)
   * @param {number} chartWidth - Width of the chart in pixels
   * @returns {number} Estimated height in pixels
   */
  if (!legendData || legendData.length === 0) {
    return 0;
  }

  // Use 85% of chart width for legend items
  const availableWidth = chartWidth * 0.85;
  const avgCharWidth = 8;
  const iconAndPadding = 35; // icon + spacing between items

  let totalWidth = 0;
  let rows = 1;

  legendData.forEach(item => {
    const itemWidth = item.name.length * avgCharWidth + iconAndPadding;

    if (totalWidth + itemWidth > availableWidth && totalWidth > 0) {
      // Start a new row if item doesn't fit
      rows++;
      totalWidth = itemWidth;
    } else {
      totalWidth += itemWidth;
    }
  });

  const itemHeight = 28; // pixels per row (including padding)
  return rows * itemHeight + 15; // 15px extra bottom padding
}

// legend: 'series' — one entry per colour: from `seriesData` (series kept only when their colour
// matches an entry, renamed from its `titles`), else from the series themselves
function seriesLegend(option, seriesData, fallbackColor) {
  const all = asArray(option.series);
  let source = all;

  if (Array.isArray(seriesData) && seriesData.length > 0) {
    source = [];
    all.forEach((series) => {
      const colour = (seriesColour(series) || '').toLowerCase();
      const match = seriesData.find((sd) => (sd.color || '').toLowerCase() === colour);
      if (!match) return;
      const titles = Array.isArray(match.titles) ? [...new Set(match.titles)] : [];
      source.push({ ...series, name: titles.length > 0 ? titles.join(', ') : match.name });
    });
  }

  if (source.length === 0) {
    option.series = source;
    return [];
  }
  const { groupedLegend, updatedSeries } = groupLegendByColor(source, fallbackColor);
  option.series = updatedSeries;
  return groupedLegend;
}

// legend: 'items' — one entry per data item of the first pie series, in its colour. The names
// stay the data items' own (the legend matches by name); only the legend's formatter shortens them.
function itemsLegend(option) {
  const pie = asArray(option.series).find((series) => series?.type === 'pie');
  if (!pie) return [];
  const entries = [];
  const seen = new Set();
  asArray(pie.data).forEach((item) => {
    const name = item && typeof item === 'object' ? item.name : null;
    if (name == null || name === '' || seen.has(String(name))) return;
    seen.add(String(name));
    const entry = { name: String(name), icon: 'circle' };
    if (typeof item.itemStyle?.color === 'string') entry.itemStyle = { color: item.itemStyle.color };
    entries.push(entry);
  });
  return entries;
}

/**
 * Builds the option a chart is exported with — PURE: no DOM, no ECharts instance, so it runs in
 * Node (ECharts SSR) as well. The input is cloned with `echarts.util.clone` (functions — axis
 * label formatters, label formatters, `itemStyle.color` functions — survive by reference) and
 * never mutated.
 *
 * The plot keeps the box it had on screen: a title band (`title`, optional `subtitle`) goes above
 * it and a legend band below it, and the canvas grows by both. An option with a dataZoom slider
 * (BaseTimeSeriesChart) keeps the pre-2026-10-16 geometry — total `height + legendHeight + 20`,
 * `grid.bottom = legendHeight + 30` — plus the title band. With no title, no legend and no slider
 * the grid is left exactly as it was.
 *
 * @param {object} option the source option (e.g. `chart.getOption()`)
 * @param {object} opts
 * @param {number} opts.width the live chart's width in px (the canvas keeps it)
 * @param {number} opts.height the live chart's height in px (the plot keeps it)
 * @param {string} [opts.mode=THEME_LIGHT] `'light'` | `'dark'` — background and text colours
 * @param {'series'|'items'|false} [opts.legend='series'] `'series'`: one entry per series colour
 *   (from `seriesData` when given); `'items'`: one entry per data item of the first pie series;
 *   `false`: no legend at all
 * @param {Array<{ name: string, titles?: string[], color?: string }>|null} [opts.seriesData=null]
 *   `'series'` only: the legend source, matched to the series by colour
 * @param {string} [opts.title=''] the title band's text; empty = no title band
 * @param {string} [opts.subtitle=''] a second, smaller line under the title (with a title only)
 * @returns {{ option: object, width: number, height: number }} the export option and canvas size
 */
export function buildExportOption(option, {
  width,
  height,
  mode = THEME_LIGHT,
  legend = 'series',
  seriesData = null,
  title = '',
  subtitle = '',
} = {}) {
  const { background, fontColor, othersColor } = chartTheme(mode);
  const out = echarts.util.clone(option ?? {});
  const canvasWidth = Number(width) > 0 ? Number(width) : 600;
  const plotHeight = Number(height) > 0 ? Number(height) : 400;

  // ---- legend entries (the names the legend matches series / pie items by)
  let legendData = [];
  if (legend === 'series') legendData = seriesLegend(out, seriesData, othersColor);
  else if (legend === 'items') legendData = itemsLegend(out);

  // The legend shows each name shortened to the canvas width; the series and items keep their
  // full names, so every entry still matches its series by name
  const shorten = (name) => truncateLegendText(String(name), canvasWidth);
  const legendHeight = calculateLegendHeight(legendData.map((item) => ({ name: shorten(item.name) })), canvasWidth);
  const legendBand = legendData.length > 0 ? legendHeight + 20 : 0;

  if (legend === false) {
    delete out.legend;
  } else if (legendData.length > 0) {
    out.legend = [{
      orient: 'horizontal',
      bottom: 10,
      left: 'center',
      data: legendData,
      formatter: shorten,
      textStyle: {
        color: fontColor,
        fontSize: 12
      },
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 15,
      pageButtonItemGap: 5,
      pageButtonGap: 5,
      pageButtonPosition: 'end'
    }];
  }

  // ---- title band
  const titleBand = title ? (subtitle ? TITLE_BAND_WITH_SUBTITLE : TITLE_BAND) : 0;
  if (title) {
    out.title = {
      text: title,
      subtext: subtitle || '',
      left: 16,
      top: 10,
      textStyle: { color: fontColor, fontSize: 16, fontWeight: 'normal', width: canvasWidth - 32, overflow: 'truncate' },
      subtextStyle: { color: fontColor, fontSize: 12, width: canvasWidth - 32, overflow: 'truncate' }
    };
  }

  // ---- the dataZoom slider (hidden in the export) decides the geometry
  const zooms = asArray(out.dataZoom);
  const hadSlider = zooms.some((zoom) => zoom && (zoom.type ?? 'slider') === 'slider' && zoom.show !== false);
  if (out.dataZoom != null) {
    out.dataZoom = zooms.map((zoom) => ({ ...zoom, show: false }));
  }

  const totalHeight = hadSlider
    ? titleBand + plotHeight + legendHeight + 20
    : titleBand + plotHeight + legendBand;

  // Without a slider the legend starts right under the plot's box instead of at the canvas
  // bottom: `calculateLegendHeight` errs on the generous side, so its slack lands below the
  // legend rather than as a gap between the plot and the legend
  if (!hadSlider && legendData.length > 0) {
    const { bottom, ...placed } = out.legend[0];
    out.legend = [{ ...placed, top: titleBand + plotHeight + 10 }];
  }

  // ---- keep the plot's live box: shift it down by the title band, make room for the legend
  if (titleBand > 0 || legendBand > 0 || hadSlider) {
    if (out.grid == null && (out.xAxis != null || out.yAxis != null)) out.grid = [{}];
    if (out.grid != null) {
      out.grid = asArray(out.grid).map((grid) => ({
        ...grid,
        top: toPx(grid.top, plotHeight, GRID_TOP_DEFAULT) + titleBand,
        bottom: hadSlider
          ? legendHeight + 30
          : toPx(grid.bottom, plotHeight, GRID_BOTTOM_DEFAULT) + legendBand
      }));
    }
    // A pie lays out in its own box (top/bottom/height, ECharts ≥ 5): pin it to the live one so
    // its centre and radius stay where they were
    if (out.series != null) {
      out.series = asArray(out.series).map((series) => {
        if (series?.type !== 'pie') return series;
        const top = toPx(series.top, plotHeight, 0);
        const boxHeight = series.height != null
          ? toPx(series.height, plotHeight, plotHeight)
          : plotHeight - top - toPx(series.bottom, plotHeight, 0);
        return { ...series, top: top + titleBand, height: boxHeight };
      });
    }
  }

  out.backgroundColor = background;
  out.animation = false;

  return { option: out, width: canvasWidth, height: totalHeight };
}

/**
 * Renders `option` off-screen with {@link buildExportOption}'s title band and legend and
 * downloads it as a PNG (2× pixel ratio, the theme's background). The hidden div and the
 * temporary chart are always cleaned up, also when the render throws; a failure is logged with
 * `console.error` and downloads nothing.
 *
 * @param {object} option the source option (e.g. `chart.getOption()`, or one built by hand)
 * @param {object} opts `filename` (default `'chart.png'`) plus every {@link buildExportOption} option
 */
export function downloadOptionAsPNG(option, {
  filename = 'chart.png',
  width,
  height,
  mode = THEME_LIGHT,
  legend = 'series',
  seriesData = null,
  title = '',
  subtitle = '',
} = {}) {
  if (!option) return;

  let built;
  try {
    built = buildExportOption(option, { width, height, mode, legend, seriesData, title, subtitle });
  } catch (error) {
    console.error('[chart-export] building the export option failed:', error);
    return;
  }
  const { background: backgroundColor } = chartTheme(mode);

  // A temporary hidden div for the off-screen render
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.width = `${built.width}px`;
  tempDiv.style.height = `${built.height}px`;
  document.body.appendChild(tempDiv);

  let tempChart = null;
  const cleanup = () => {
    try {
      tempChart?.dispose();
    } catch (error) {
      console.error('[chart-export] disposing the export chart failed:', error);
    }
    tempDiv.remove();
  };

  let scheduled = false;
  try {
    tempChart = echarts.init(tempDiv);
    tempChart.setOption(built.option);
    // Force resize to ensure proper layout
    tempChart.resize();

    // A small delay to let the render complete
    setTimeout(() => {
      let url = null;
      try {
        url = tempChart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor
        });
      } catch (error) {
        console.error('[chart-export] rendering the PNG failed:', error);
      } finally {
        cleanup();
      }
      if (!url) return;

      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
    }, 100);
    scheduled = true;
  } catch (error) {
    console.error('[chart-export] rendering the export chart failed:', error);
  } finally {
    if (!scheduled) cleanup();
  }
}

/**
 * Renders `chart` off-screen with a generated legend (and an optional title band) and downloads
 * it as a PNG — {@link downloadOptionAsPNG} over the live chart's option and size. The signature
 * is the pre-2026-10-16 one plus the optional `legend`/`title`/`subtitle`.
 *
 * The export colours come from the design tokens, not from the live DOM: pass the
 * theme the page is currently in. Callers inside a component get it from
 * `useChartTheme().value.mode` (`@rdrive/theme`); anything not exactly `'dark'`
 * falls back to light.
 *
 * @param {import('echarts/core').ECharts} chart the live ECharts instance
 * @param {string} [filename='chart.png'] download file name
 * @param {Array<{ name: string, titles?: string[], color?: string }>|null} [seriesData=null]
 *   optional legend source; series are matched to it by colour
 * @param {{ mode?: string, legend?: 'series'|'items'|false, title?: string, subtitle?: string }} [options]
 *   `mode` — `'light'` or `'dark'`; the rest as in {@link buildExportOption}
 */
export function downloadChartAsPNG(chart, filename = 'chart.png', seriesData = null, {
  mode = THEME_LIGHT,
  legend = 'series',
  title = '',
  subtitle = '',
} = {}) {
  if (!chart) return;
  downloadOptionAsPNG(chart.getOption(), {
    filename,
    width: chart.getWidth(),
    height: chart.getHeight(),
    mode,
    legend,
    seriesData,
    title,
    subtitle,
  });
}
